import { useMemo, useState } from "react";
import { FileText, Search } from "lucide-react";
import { Footer } from "@/components/Footer";
import { SmartLink } from "@/components/Header";
import { Reveal } from "@/components/site/Reveal";
import { PageMeta } from "@/components/site/blocks";
import { resourceGroups } from "@/data/resources";

const featured = [
  { title: "Documentation", body: "Set up your workspace and connect your accounts.", href: "/documentation" },
  { title: "Security architecture", body: "How ClearMakes protects your data.", href: "/security-architecture" },
  { title: "Guides", body: "Step-by-step guides for email and chat work.", href: "/guides" },
];

// Resources reads like a page index in a workspace: grouped, searchable, plain.
const Resources = () => {
  const [q, setQ] = useState("");
  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return resourceGroups;
    return resourceGroups
      .map((g) => ({ ...g, links: g.links.filter((l) => l.label.toLowerCase().includes(needle)) }))
      .filter((g) => g.links.length);
  }, [q]);

  return (
    <>
      <PageMeta
        title="ClearMakes Resources: Guides, Comparisons and How-tos"
        description="Guides, comparisons and how-tos from ClearMakes: automating email and chat work, building AI agents from your documents, and getting help in meetings."
        path="/resources"
      />
      <main className="bg-background text-foreground">
        <section className="pb-10 pt-16 sm:pt-24">
          <div className="cm-container">
            <Reveal>
              <p className="cm-eyebrow mb-3">Resources</p>
              <h1 className="cm-display max-w-3xl">Guides and answers.</h1>
              <p className="cm-lead mt-6 max-w-2xl">Everything we have written about getting more from ClearMakes products.</p>
            </Reveal>
            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              {featured.map((f) => (
                <SmartLink key={f.href} href={f.href} className="cm-card cm-card-hover p-6">
                  <h2 className="cm-h3">{f.title}</h2>
                  <p className="cm-body mt-2">{f.body}</p>
                </SmartLink>
              ))}
            </div>
          </div>
        </section>

        <section className="pb-24">
          <div className="cm-container">
            <label className="relative block max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search resources"
                aria-label="Search resources"
                className="h-11 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-[15px] placeholder:text-muted-foreground focus:border-foreground focus:outline-none"
              />
            </label>

            <div className="mt-10 space-y-12">
              {groups.map((g) => (
                <div key={g.heading}>
                  <h2 className="border-b border-border pb-3 text-[13px] font-semibold text-muted-foreground">
                    {g.heading} <span className="font-normal">· {g.links.length}</span>
                  </h2>
                  <ul className="mt-2 grid grid-cols-[minmax(0,1fr)] gap-x-6 sm:grid-cols-2 lg:grid-cols-3 [&>li]:min-w-0">
                    {g.links.map((l) => (
                      <li key={l.href}>
                        <SmartLink href={l.href} className="flex items-center gap-2.5 rounded-md px-2 py-2 text-[15px] text-foreground transition-colors hover:bg-muted">
                          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
                          <span className="min-w-0 sm:truncate">{l.label}</span>
                        </SmartLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {groups.length === 0 && <p className="cm-body">Nothing matches “{q}”.</p>}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default Resources;
